import type { UniqJob } from '@masha/shared';
import { uniqErrorText } from '@masha/shared';

/**
 * Read duration client-side so obviously-too-long clips are rejected before a
 * 100 MB upload. Resolves null when the browser cannot read metadata in time —
 * the worker is the authority anyway.
 */
export function readVideoDuration(file: File, timeoutMs = 3000): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    let done = false;
    const finish = (value: number | null) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);
    video.onloadedmetadata = () =>
      finish(Number.isFinite(video.duration) ? Math.ceil(video.duration) : null);
    video.onerror = () => finish(null);
    video.src = url;
  });
}

export function formatRetryAfter(sec: number): string {
  const h = Math.floor(sec / 3600);
  if (h >= 1) return `через ${h} ч`;
  const m = Math.max(1, Math.ceil(sec / 60));
  return `через ${m} мин`;
}

export function uniqStatusText(job: UniqJob, maxDurationSec: number): string {
  switch (job.status) {
    case 'queued':
      return 'В очереди';
    case 'processing':
      return `Обрабатываем ${job.copiesDone} из ${job.copiesTotal}`;
    case 'done':
      return job.deliveryError
        ? 'Готово. В чат отправить не удалось, скачай по ссылкам'
        : 'Готово. Копии отправлены в чат с ботом';
    case 'expired':
      return 'Файлы удалены через 24 часа';
    case 'failed':
      return uniqErrorText(job.errorCode, { maxDurationSec });
  }
}

export const isActiveJob = (job: UniqJob) => job.status === 'queued' || job.status === 'processing';

/** Short label for the status pill; the full sentence goes below it. */
export function uniqBadgeLabel(job: UniqJob): string {
  switch (job.status) {
    case 'queued':
      return 'В очереди';
    case 'processing':
      return `${job.copiesDone}/${job.copiesTotal}`;
    case 'done':
      return 'Готово';
    case 'failed':
      return 'Ошибка';
    case 'expired':
      return 'Удалено';
  }
}
